import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import inventoryService from '../../services/inventoryService';
import toast from 'react-hot-toast';

export const fetchGRNs = createAsyncThunk(
  'inventory/fetchGRNs',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await inventoryService.getGRNs(params);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch GRNs');
    }
  }
);

export const fetchGRN = createAsyncThunk(
  'inventory/fetchGRN',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await inventoryService.getGRN(id);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch GRN');
    }
  }
);

export const createGRN = createAsyncThunk(
  'inventory/createGRN',
  async (grnData, { rejectWithValue }) => {
    try {
      const { data } = await inventoryService.createGRN(grnData);
      toast.success('GRN created successfully');
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to create GRN');
    }
  }
);

export const fetchInventory = createAsyncThunk(
  'inventory/fetchInventory',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await inventoryService.getInventory(params);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch inventory');
    }
  }
);

export const fetchDispatches = createAsyncThunk(
  'inventory/fetchDispatches',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await inventoryService.getDispatches(params);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch dispatches');
    }
  }
);

export const createDispatch = createAsyncThunk(
  'inventory/createDispatch',
  async (dispatchData, { rejectWithValue }) => {
    try {
      const { data } = await inventoryService.createDispatch(dispatchData);
      toast.success('Dispatch order created');
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to create dispatch');
    }
  }
);

const initialState = {
  grns: [],
  currentGRN: null,
  grnTotalCount: 0,
  grnLoading: false,
  inventory: [],
  inventoryTotalCount: 0,
  inventoryLoading: false,
  dispatches: [],
  dispatchTotalCount: 0,
  dispatchLoading: false,
  movements: [],
  error: null,
};

const inventorySlice = createSlice({
  name: 'inventory',
  initialState,
  reducers: {
    clearCurrentGRN: (state) => {
      state.currentGRN = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGRNs.pending, (state) => {
        state.grnLoading = true;
      })
      .addCase(fetchGRNs.fulfilled, (state, action) => {
        state.grnLoading = false;
        state.grns = (() => { const _d = action.payload; return Array.isArray(_d?.results) ? _d.results : Array.isArray(_d?.items) ? _d.items : Array.isArray(_d) ? _d : []; })();
        state.grnTotalCount = action.payload.count || 0;
      })
      .addCase(fetchGRNs.rejected, (state, action) => {
        state.grnLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchGRN.fulfilled, (state, action) => {
        state.currentGRN = action.payload;
      })
      .addCase(createGRN.fulfilled, (state, action) => {
        state.grns.unshift(action.payload);
      })
      .addCase(fetchInventory.pending, (state) => {
        state.inventoryLoading = true;
      })
      .addCase(fetchInventory.fulfilled, (state, action) => {
        state.inventoryLoading = false;
        state.inventory = (() => { const _d = action.payload; return Array.isArray(_d?.results) ? _d.results : Array.isArray(_d?.items) ? _d.items : Array.isArray(_d) ? _d : []; })();
        state.inventoryTotalCount = action.payload.count || 0;
      })
      .addCase(fetchInventory.rejected, (state) => {
        state.inventoryLoading = false;
      })
      .addCase(fetchDispatches.pending, (state) => {
        state.dispatchLoading = true;
      })
      .addCase(fetchDispatches.fulfilled, (state, action) => {
        state.dispatchLoading = false;
        state.dispatches = (() => { const _d = action.payload; return Array.isArray(_d?.results) ? _d.results : Array.isArray(_d?.items) ? _d.items : Array.isArray(_d) ? _d : []; })();
        state.dispatchTotalCount = action.payload.count || 0;
      })
      .addCase(fetchDispatches.rejected, (state) => {
        state.dispatchLoading = false;
      })
      .addCase(createDispatch.fulfilled, (state, action) => {
        state.dispatches.unshift(action.payload);
      });
  },
});

export const { clearCurrentGRN, clearError } = inventorySlice.actions;
export default inventorySlice.reducer;
